import React, { useState } from 'react';
import { Button, Form, Space } from 'antd';
import {
  Link,
  Drawer,
  ErrorHandler,
  LoadingState,
  Tabs,
} from '@common/components';
import { ORGANIZATION_ADMIN_MENU } from 'works/legacy/pages/organization/admin-settings/organization-admin-vertical-nav-menu';
import {
  WorksMembershipArgs,
  WorksMembershipQuery,
  WorksMembershipResponse,
} from 'works/legacy/graphql/works-membership';
import { useMutation, useQuery, useNavigate } from '@common/hooks';
import { useCurrentMembership } from '@identity/hooks';
import UpdateMembershipStatusModal from './update-membership-status-modal';
import { MemberViewDetails } from './member-view-details';
import { MemberViewLocations } from './member-view-locations';
import { MemberViewPermissions } from './member-view-permissions';
import { createMessage } from 'works/legacy/lib/message-helper';
import { UpdateWorksMembershipPermissions } from '@identity/graphql/mutations/membership-permissions';
import {
  WorksMembershipUpdate,
  WorksMembershipUpdateResponse,
} from 'works/legacy/graphql/works-membership-update';
import auth from 'works/legacy/lib/auth';

type EntityType = 'organization' | 'supplier';

export const MemberViewDrawer = ({
  open,
  onClose,
  userId,
}: {
  open: boolean;
  onClose: () => void;
  userId?: string;
}) => {
  const currentMembership = useCurrentMembership();
  const entityType = auth.membershipType()?.toLowerCase() as EntityType;

  const navigate = useNavigate();

  const [tab, setTab] = useState('details');
  const [editPermissions, setEditPermissions] = useState(false);
  const [editDetails, setEditDetails] = useState(false);
  const [changedPermissions, setChangedPermissions] = useState<
    Record<string, boolean>
  >({});
  const [showUpdateModal, setShowUpdateModal] = useState(false);

  const [form] = Form.useForm();

  const [updateMembership, { loading: loadingUpdateDetails }] =
    useMutation<WorksMembershipUpdateResponse>(WorksMembershipUpdate, {
      onCompleted: () => {
        createMessage('success', `User details have been updated.`);
        setEditDetails(false);
        form.resetFields();
      },
      onError: () => {
        createMessage(
          'error',
          'User details could not be updated. Please try again or contact your hiring partner.'
        );
      },
      refetchQueries: ['WorksMembership'],
    });

  const {
    data: dataRetrievedMembership,
    loading: loadingRetrievedMembership,
    error: errorRetrievedMembership,
  } = useQuery<WorksMembershipResponse, WorksMembershipArgs>(
    WorksMembershipQuery,
    {
      skip: !userId,
      variables: {
        id: userId || '',
      },
    }
  );

  const [updatePermissions, { loading: loadingPermissions }] = useMutation(
    UpdateWorksMembershipPermissions,
    {
      onCompleted: () => {
        createMessage('success', `User permissions have been updated.`);
        setEditPermissions(false);
        setChangedPermissions({});
      },
      onError: () => {
        createMessage(
          'error',
          'User permissions could not be updated. Please try again or contact your hiring partner.'
        );
      },
      refetchQueries: ['WorksMembershipPermissions', 'WorksMembershipsQuery'],
    }
  );

  if (errorRetrievedMembership) {
    return (
      <ErrorHandler
        error={errorRetrievedMembership}
        log="MemberViewDrawer"
        messageType="contextual"
      />
    );
  }

  const membership = dataRetrievedMembership?.worksMembership;

  const showFooter = currentMembership.canUpdateMembership;

  const organizationUnits = membership?.organizationUnitsActive || [];
  const emptyUnits = organizationUnits.length === 0;

  const isOrganization = entityType === 'organization';

  function redirectToAssignLocations() {
    navigate(`${ORGANIZATION_ADMIN_MENU.USERS.ROUTE}/update-locations`, {
      state: {
        memberIds: [membership?.id],
      },
    });
  }

  const showEditPermissions = tab === 'permissions' && editPermissions;

  const onUpdatedPermissions = () => {
    const permissions = Object.entries(changedPermissions).map(
      ([key, value]) => ({
        permissionName: key,
        enabled: value,
      })
    );

    updatePermissions({
      variables: {
        input: {
          id: membership?.id,
          permissions,
        },
      },
    });
  };

  const onUpdateDetails = () => {
    // Removed the async/await to avoid the browser freezing - looks like a bug in @babel/core
    form
      .validateFields()
      .then((valuesToSave) => {
        const worksMembershipData = {
          email: valuesToSave?.email,
          employeeId: valuesToSave?.employeeId,
          firstName: valuesToSave?.firstName,
          lastName: valuesToSave?.lastName,
          role: valuesToSave?.role,
          phone: valuesToSave?.phone,
          timeZone: valuesToSave?.timeZone,
          title: valuesToSave?.title,
          organizationUnits: null,
        };

        updateMembership({
          variables: { id: membership?.id, input: worksMembershipData },
        });
      })
      .catch(() => {
        return false;
      });
  };

  const footer = !loadingRetrievedMembership && membership && showFooter && (
    <Space>
      {!showEditPermissions && !editDetails && (
        <Button
          size="large"
          danger={membership?.active}
          onClick={() => setShowUpdateModal(true)}
        >
          {membership?.active ? 'Deactivate' : 'Reactivate'}
        </Button>
      )}
      {!!membership?.active && isOrganization && tab === 'permissions' && (
        <>
          {showEditPermissions ? (
            <>
              <Button
                size="large"
                onClick={() => {
                  setEditPermissions(false);
                  setChangedPermissions({});
                }}
                disabled={loadingPermissions}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                size="large"
                onClick={() => onUpdatedPermissions()}
                loading={loadingPermissions}
              >
                Save Permissions
              </Button>
            </>
          ) : (
            <Button size="large" onClick={() => setEditPermissions(true)}>
              Edit Permissions
            </Button>
          )}
        </>
      )}
      {!!membership?.active && tab === 'details' && (
        <>
          {editDetails ? (
            <>
              <Button
                size="large"
                onClick={() => {
                  setEditDetails(false);
                  form.resetFields();
                }}
                disabled={loadingUpdateDetails}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                size="large"
                onClick={() => onUpdateDetails()}
                loading={loadingUpdateDetails}
              >
                Save Details
              </Button>
            </>
          ) : (
            <Button
              size="large"
              onClick={() => {
                setEditDetails(true);
                form.setFieldsValue({
                  firstName: membership.firstName,
                  lastName: membership.lastName,
                  email: membership.email,
                  employeeId: membership.employeeId,
                  phone: membership.phone,
                  role: membership.role,
                  title: membership.title,
                  timeZone: membership.timeZone
                    ? membership.timeZone
                    : membership?.entity?.defaultTimeZone,
                });
              }}
            >
              Edit Details
            </Button>
          )}
        </>
      )}
      {!!membership?.active && isOrganization && tab === 'locations' && (
        <Link
          to={`${ORGANIZATION_ADMIN_MENU.USERS.ROUTE}/edit/${membership.email}?step=2`}
        >
          <Button size="large">Edit Locations</Button>
        </Link>
      )}
      {isOrganization && emptyUnits && (
        <Button size="large" type="primary" onClick={redirectToAssignLocations}>
          Assign Locations
        </Button>
      )}
    </Space>
  );

  const tabItems = [
    {
      key: 'details',
      label: 'Details',
      children: (
        <MemberViewDetails
          form={form}
          membership={membership}
          editMode={editDetails}
          entityType={entityType}
        />
      ),
    },
    ...(membership?.entity.onDemandEnabled
      ? [
          {
            key: 'permissions',
            label: 'Permissions',
            children: (
              <MemberViewPermissions
                membership={membership}
                changedPermissions={changedPermissions}
                setChangedPermissions={setChangedPermissions}
                allowEdit={editPermissions}
              />
            ),
          },
        ]
      : []),
    {
      key: 'locations',
      label: 'Locations',
      children: <MemberViewLocations membership={membership} />,
    },
  ];

  return (
    <Drawer
      open={open}
      className="admin-member-view-drawer"
      title={membership?.name || 'Loading...'}
      footer={footer}
      onClose={() => {
        onClose();
        setEditDetails(false);
        setEditPermissions(false);
        setChangedPermissions({});
        setTab('details');
      }}
      bodyStyle={{
        paddingTop: 6,
      }}
    >
      <UpdateMembershipStatusModal
        open={showUpdateModal}
        setOpen={setShowUpdateModal}
        membership={membership}
      />
      {loadingRetrievedMembership || !membership ? (
        <LoadingState />
      ) : isOrganization ? (
        <Tabs
          className="MemberViewDrawerTabs"
          onChange={setTab}
          activeKey={tab}
          items={tabItems}
        />
      ) : (
        <MemberViewDetails
          form={form}
          membership={membership}
          editMode={editDetails}
          entityType={entityType}
        />
      )}
    </Drawer>
  );
};
